import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ArrowRight, Check, MapPin } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { getYouTubeId } from '../venue-story/utils';
import { AvailabilitySection } from '../venue-story/AvailabilitySection';
import { DecorCalculator } from '../venue-story/DecorCalculator';
import { CostCalculator } from '../venue-story/CostCalculator';

const Kicker = ({ children, light }) => children
  ? <p className={`uppercase tracking-[0.3em] text-[10px] md:text-xs font-bold ${light ? 'text-[#C6A87C]' : 'text-[#C6A87C]'}`}>{children}</p> : null;

const Heading = ({ children, light, center }) => children
  ? <h2 className={`font-wvc-display text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.05] mt-4 ${light ? 'text-[#FDFCF8]' : 'text-[#0A1710]'} ${center ? 'text-center' : ''}`}>{children}</h2> : null;

const Shell = ({ children, className = '' }) => (
  <section className={`py-12 md:py-20 font-wvc-body ${className}`}>
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10">{children}</div>
  </section>
);

const TextBlock = ({ data }) => (
  <Shell className="bg-[#FDFCF8]">
    <div className={data.align === 'center' ? 'max-w-3xl mx-auto text-center' : 'max-w-3xl'}>
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      {data.body && <p className="text-[#1A3626]/70 mt-5 text-sm md:text-base font-light leading-relaxed whitespace-pre-line">{data.body}</p>}
    </div>
  </Shell>
);

const CTAButton = ({ label, url, onEnquire, dark }) => {
  const navigate = useNavigate();
  if (!label) return null;
  const cls = dark
    ? 'bg-[#FDFCF8] text-[#1A3626] hover:bg-[#C6A87C] hover:text-[#0A1710]'
    : 'bg-[#1A3626] text-[#FDFCF8] hover:bg-[#C6A87C] hover:text-[#0A1710]';
  const onClick = () => {
    if (!url) return onEnquire && onEnquire();
    if (url.startsWith('http')) window.open(url, '_blank');
    else navigate(url);
  };
  return (
    <button onClick={onClick} className={`mt-7 inline-flex items-center gap-2 px-9 py-4 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${cls}`}>
      {label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
};

const TextImage = ({ data, onEnquire }) => (
  <Shell className="bg-[#FDFCF8]">
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center`}>
      <div className={`lg:col-span-6 ${data.image_side === 'left' ? 'lg:order-2' : ''}`}>
        <Kicker>{data.kicker}</Kicker>
        <Heading>{data.heading}</Heading>
        {data.body && <p className="text-[#1A3626]/70 mt-5 text-sm font-light leading-relaxed whitespace-pre-line max-w-lg">{data.body}</p>}
        <CTAButton label={data.cta_label} url={data.cta_url} onEnquire={onEnquire} />
      </div>
      {data.image && (
        <div className={`lg:col-span-6 aspect-[4/3] overflow-hidden bg-[#F3EFEB] border border-[#E5DCCF] ${data.image_side === 'left' ? 'lg:order-1' : ''}`}>
          <img src={data.image} alt={data.heading || 'Section'} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
    </div>
  </Shell>
);

const CardGrid = ({ data, onEnquire }) => {
  const cols = { '2': 'md:grid-cols-2', '3': 'md:grid-cols-3', '4': 'md:grid-cols-4' }[data.columns || '3'];
  return (
    <Shell className="bg-[#FDFCF8]">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      {data.intro && <p className="text-[#1A3626]/70 mt-4 text-sm font-light max-w-2xl">{data.intro}</p>}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols} gap-5 mt-9`}>
        {(data.cards || []).map((c, i) => (
          <div key={i} className="bg-white border border-[#E5DCCF] group flex flex-col" data-testid={`card-grid-item-${i}`}>
            {c.image && (
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
              </div>
            )}
            <div className="p-5 flex-1 flex flex-col">
              <h3 className="font-wvc-display text-xl md:text-2xl text-[#0A1710]">{c.title}</h3>
              {c.price && <p className="text-[10px] uppercase tracking-[0.2em] text-[#C6A87C] font-bold mt-1.5">{c.price}</p>}
              {c.text && <p className="text-[13px] text-[#1A3626]/70 mt-3 leading-relaxed font-light flex-1">{c.text}</p>}
              {c.cta_label && <div className="-mt-2"><CTAButton label={c.cta_label} url={c.cta_url} onEnquire={onEnquire} /></div>}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
};

const ImageBanner = ({ data }) => (
  <section className="relative font-wvc-body">
    <div className={`${data.height === 'tall' ? 'h-[62vh] md:h-[78vh]' : 'h-[42vh] md:h-[55vh]'} overflow-hidden bg-[#0A1710]`}>
      {data.image && <img src={data.image} alt={data.heading || 'Banner'} className="w-full h-full object-cover" loading="lazy" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1710]/75 via-transparent to-transparent" />
    </div>
    {(data.heading || data.caption) && (
      <div className="absolute bottom-8 left-0 right-0">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          {data.heading && <h2 className="font-wvc-display text-3xl md:text-5xl text-[#FDFCF8] font-light tracking-tight">{data.heading}</h2>}
          {data.caption && <p className="text-[#FDFCF8]/70 text-sm mt-2 font-light">{data.caption}</p>}
        </div>
      </div>
    )}
  </section>
);

const GalleryGrid = ({ data }) => {
  const cols = { '2': 'md:grid-cols-2', '3': 'md:grid-cols-3', '4': 'md:grid-cols-4' }[data.columns || '3'];
  return (
    <Shell className="bg-[#FDFCF8]">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      <div className={`grid grid-cols-2 ${cols} gap-3 mt-8`}>
        {(data.photos || []).map((url, i) => (
          <div key={i} className="aspect-[4/3] overflow-hidden bg-[#F3EFEB]">
            <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
        ))}
      </div>
    </Shell>
  );
};

const VideoSection = ({ data }) => {
  const vid = getYouTubeId(data.video_url);
  if (!vid) return null;
  return (
    <Shell className="bg-[#FDFCF8]">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-5">
          <Kicker>{data.kicker}</Kicker>
          <Heading>{data.heading}</Heading>
          {data.caption && <p className="text-[#1A3626]/70 mt-5 text-sm font-light leading-relaxed max-w-md">{data.caption}</p>}
        </div>
        <div className="lg:col-span-7 aspect-video bg-[#F3EFEB] overflow-hidden border border-[#E5DCCF]">
          <iframe
            src={`https://www.youtube.com/embed/${vid}?rel=0&cc_load_policy=0`}
            title={data.heading || 'Video'}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </Shell>
  );
};

const VideoStrip = ({ data }) => {
  const [playing, setPlaying] = useState(null);
  const videos = (data.videos || []).map(getYouTubeId).filter(Boolean);
  if (!videos.length) return null;
  return (
    <Shell className="bg-[#F3EFEB]">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      {data.intro && <p className="text-[#1A3626]/70 mt-4 text-sm font-light max-w-2xl">{data.intro}</p>}
      <div className="mt-8 flex gap-4 overflow-x-auto pb-3 [scrollbar-width:thin] snap-x">
        {videos.map((vid, i) => (
          <div key={vid} className="w-[280px] md:w-[360px] shrink-0 aspect-video overflow-hidden bg-[#0A1710] relative group snap-start" data-testid={`video-strip-item-${i}`}>
            {playing === vid ? (
              <iframe
                src={`https://www.youtube.com/embed/${vid}?rel=0&autoplay=1&cc_load_policy=0`}
                title={`Video ${i + 1}`}
                className="w-full h-full border-0"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button onClick={() => setPlaying(vid)} className="w-full h-full relative">
                <img src={`https://i.ytimg.com/vi/${vid}/hqdefault.jpg`} alt={`Video ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute inset-0 flex items-center justify-center bg-[#0A1710]/25 group-hover:bg-[#0A1710]/45 transition-colors">
                  <span className="w-12 h-12 rounded-full bg-[#FDFCF8]/95 flex items-center justify-center">
                    <Play className="w-4 h-4 text-[#1A3626] ml-0.5" fill="currentColor" />
                  </span>
                </span>
              </button>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
};

const Quote = ({ data }) => (
  <Shell className="bg-[#FDFCF8]">
    <div className="max-w-3xl">
      <span className="font-wvc-display text-6xl md:text-7xl text-[#C6A87C] leading-none">“</span>
      <p className="font-wvc-display text-2xl sm:text-3xl md:text-4xl font-light text-[#0A1710] tracking-tight leading-[1.2] -mt-4">{data.quote}</p>
      {data.attribution && <p className="text-[10px] uppercase tracking-[0.25em] text-[#C6A87C] font-bold mt-5">— {data.attribution}</p>}
    </div>
  </Shell>
);

const CtaBanner = ({ data, onEnquire }) => {
  const dark = data.style !== 'light';
  return (
    <section className={`${dark ? 'bg-[#1A3626]' : 'bg-[#F3EFEB] border-y border-[#E5DCCF]'} py-14 md:py-20 font-wvc-body`}>
      <div className="max-w-3xl mx-auto px-5 sm:px-10 text-center">
        <h2 className={`font-wvc-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05] ${dark ? 'text-[#FDFCF8]' : 'text-[#0A1710]'}`}>{data.heading}</h2>
        {data.copy && <p className={`mt-4 text-sm font-light leading-relaxed ${dark ? 'text-[#FDFCF8]/60' : 'text-[#1A3626]/70'}`}>{data.copy}</p>}
        <CTAButton label={data.cta_label} url={data.cta_url} onEnquire={onEnquire} dark={dark} />
      </div>
    </section>
  );
};

const StatsRow = ({ data }) => (
  <section className="bg-[#1A3626] border-y border-[#2A4D38] font-wvc-body">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-2 md:grid-cols-4">
      {(data.stats || []).map((s, i) => (
        <div key={i} className={`py-8 md:py-12 px-5 border-[#2A4D38] ${i > 0 ? 'border-l' : ''} ${i >= 2 ? 'max-md:border-t max-md:[&:nth-child(3)]:border-l-0' : ''}`}>
          <div className="font-wvc-display text-4xl md:text-6xl font-light text-[#FDFCF8] tracking-tight tabular-nums">{s.value}</div>
          <div className="text-[9px] md:text-[10px] uppercase tracking-[0.25em] font-bold text-[#C6A87C] mt-3">{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

const FeatureChecklist = ({ data }) => {
  const cols = { '1': '', '2': 'sm:grid-cols-2', '3': 'sm:grid-cols-3' }[data.columns || '2'];
  return (
    <Shell className="bg-[#FDFCF8]">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      <div className={`grid grid-cols-1 ${cols} gap-x-10 mt-8 border-t border-[#E5DCCF]`}>
        {(data.items || []).map((item, i) => (
          <div key={i} className="flex items-start gap-4 py-4 border-b border-[#E5DCCF]">
            <Check className="w-4 h-4 text-[#C6A87C] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#1A3626]/80 leading-relaxed font-light">{item}</p>
          </div>
        ))}
      </div>
    </Shell>
  );
};

const FaqAccordion = ({ data }) => (
  <Shell className="bg-[#FDFCF8]">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <Kicker>{data.kicker}</Kicker>
        <Heading>{data.heading}</Heading>
      </div>
      <div className="lg:col-span-8">
        <Accordion type="single" collapsible className="border-t border-[#E5DCCF]">
          {(data.items || []).map((f, i) => (
            <AccordionItem key={i} value={`bfaq-${i}`} className="border-b border-[#E5DCCF]">
              <AccordionTrigger className="text-left font-wvc-display text-xl md:text-2xl font-light text-[#0A1710] py-5 hover:no-underline hover:text-[#1A3626]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#1A3626]/70 text-sm font-light leading-relaxed pb-5 max-w-2xl">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </Shell>
);

const MapEmbed = ({ data }) => {
  if (!data.address) return null;
  return (
    <Shell className="bg-[#FDFCF8]">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      <div className="mt-8 aspect-[16/7] max-md:aspect-[4/3] overflow-hidden border border-[#E5DCCF] bg-[#F3EFEB]">
        <iframe
          src={`https://www.google.com/maps?q=${encodeURIComponent(data.address)}&output=embed`}
          title={data.heading || 'Map'}
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      {data.note && <p className="text-[#1A3626]/60 text-xs mt-3 font-light flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#C6A87C]" strokeWidth={1.5} /> {data.note}</p>}
    </Shell>
  );
};

const MeetTeam = ({ data }) => (
  <Shell className="bg-[#FDFCF8]">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
      <div className="lg:col-span-7">
        <Kicker>{data.kicker}</Kicker>
        <Heading>{data.question}</Heading>
      </div>
      {data.intro && <p className="lg:col-span-5 text-[#1A3626]/70 text-sm font-light leading-relaxed">{data.intro}</p>}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
      {(data.members || []).map((m, i) => (
        <div key={i} className="group text-center" data-testid={`team-member-${i}`}>
          {m.photo_url && (
            <div className="aspect-[3/4] overflow-hidden rounded-t-full bg-[#F3EFEB] mx-auto max-w-[260px]">
              <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
            </div>
          )}
          <h3 className="font-wvc-display text-xl md:text-2xl text-[#0A1710] mt-4">{m.name}</h3>
          <p className="text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-[#C6A87C] font-bold mt-1.5">{m.role}</p>
          {m.description && <p className="text-[13px] text-[#1A3626]/70 mt-3 leading-relaxed font-light hidden sm:block max-w-xs mx-auto">{m.description}</p>}
        </div>
      ))}
    </div>
  </Shell>
);

const WeddingAlbum = ({ data }) => {
  const chapters = data.chapters || [];
  const [active, setActive] = useState(0);
  if (!chapters.length) return null;
  return (
    <Shell className="bg-[#F3EFEB]">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      {data.note && <p className="text-[#1A3626]/70 mt-4 text-sm font-light max-w-2xl">{data.note}</p>}
      <div className="mt-7 -mx-5 sm:mx-0 px-5 sm:px-0 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {chapters.map((c, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`px-5 py-2 text-[11px] uppercase tracking-[0.18em] font-bold whitespace-nowrap transition-colors ${active === i ? 'bg-[#1A3626] text-[#FDFCF8]' : 'bg-white border border-[#E5DCCF] text-[#1A3626]/70 hover:border-[#C6A87C]'}`}
            data-testid={`album-chapter-${i}`}
          >
            {c.name}
          </button>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
        {(chapters[active]?.photos || []).map((url, i) => (
          <div key={i} className="aspect-[4/3] overflow-hidden bg-white">
            <img src={url} alt={`${chapters[active]?.name} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
        ))}
      </div>
    </Shell>
  );
};

const CelebrationsHosted = ({ data }) => (
  <Shell className="bg-[#FDFCF8]">
    <Kicker>{data.kicker}</Kicker>
    <Heading>{data.heading}</Heading>
    {data.intro && <p className="text-[#1A3626]/70 mt-4 text-sm font-light max-w-2xl">{data.intro}</p>}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5DCCF] border border-[#E5DCCF] mt-9">
      {(data.items || []).map((item, i) => (
        <div key={i} className="bg-[#FDFCF8] hover:bg-[#F3EFEB] transition-colors p-7">
          <span className="block w-2.5 h-2.5 rotate-45 bg-[#C6A87C]" />
          <h3 className="font-wvc-display text-xl md:text-2xl text-[#0A1710] mt-4">{item.title}</h3>
          {item.description && <p className="text-[13px] text-[#1A3626]/70 mt-2.5 leading-relaxed font-light">{item.description}</p>}
        </div>
      ))}
    </div>
  </Shell>
);

const CustomHtml = ({ data }) => (
  <section className="font-wvc-body max-w-[1400px] mx-auto px-5 sm:px-10 py-10" dangerouslySetInnerHTML={{ __html: data.html || '' }} />
);

const Divider = ({ data }) => {
  const h = { small: 'py-4', medium: 'py-8', large: 'py-14' }[data.size || 'medium'];
  return (
    <div className={`bg-[#FDFCF8] ${h}`}>
      {data.style !== 'space' && <div className="max-w-[1400px] mx-auto px-5 sm:px-10"><div className="h-px bg-[#E5DCCF]" /></div>}
    </div>
  );
};

const AvailabilityCalendarSection = ({ data, page, onEnquire }) => {
  const decorSec = (page.sections || []).find(s => s.type === 'decor_calculator' && s.visible !== false);
  const availability = { ...(page.availability || {}), intro: data.intro || page.availability?.intro };
  return <AvailabilitySection availability={availability} decorBasePrice={decorSec?.data?.base_price || 0} onEnquire={onEnquire} />;
};

const DecorCalculatorSection = ({ data, page }) => {
  const decor = {
    question: data.question, kicker: data.kicker, title: data.title, intro: data.intro,
    base_package: { name: data.base_name || 'Base decor package', price: Number(data.base_price) || 0, inclusions: data.base_inclusions || [] },
    addons: data.addons || [], disclaimer: data.disclaimer
  };
  return <DecorCalculator decor={decor} venueName={page.name} />;
};

const CostCalculatorSection = ({ page, onEnquire }) => (
  <CostCalculator slug={page.slug} venueName={page.name} onEnquire={onEnquire} bookedDates={page.availability?.booked_dates} />
);

const RENDERERS = {
  text_block: TextBlock,
  text_image: TextImage,
  card_grid: CardGrid,
  image_banner: ImageBanner,
  gallery_grid: GalleryGrid,
  video: VideoSection,
  video_strip: VideoStrip,
  quote: Quote,
  cta_banner: CtaBanner,
  stats_row: StatsRow,
  feature_checklist: FeatureChecklist,
  faq_accordion: FaqAccordion,
  map_embed: MapEmbed,
  meet_team: MeetTeam,
  wedding_album: WeddingAlbum,
  celebrations_hosted: CelebrationsHosted,
  custom_html: CustomHtml,
  divider: Divider,
  availability_calendar: AvailabilityCalendarSection,
  decor_calculator: DecorCalculatorSection,
  cost_calculator: CostCalculatorSection
};

export const SectionRenderer = ({ section, page, onEnquire }) => {
  if (!section || section.visible === false) return null;
  const Renderer = RENDERERS[section.type];
  if (!Renderer) return null;
  return (
    <div data-testid={`page-section-${section.type}`} data-section-id={section.id}>
      <Renderer data={section.data || {}} page={page} onEnquire={onEnquire} />
    </div>
  );
};
